import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, StatCard, StatusBadge } from "@/components/domain/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Building2, DollarSign, TrendingUp, HardDrive, Plus, ArrowLeft, LogOut, ShieldPlus, Crown } from "lucide-react";

const PLAN_CLS = { Enterprise: "bg-violet-50 text-violet-700", Pro: "bg-blue-50 text-blue-700", Starter: "bg-slate-100 text-slate-600" };
const emptyT = { name: "", plan: "Starter", subdomain: "", city: "", owner_email: "", owner_password: "" };

export default function Saas() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState({ tenants: 0, active: 0, mrr: 0, arpa: 0, storage_gb: 0, items: [] });
  const [admins, setAdmins] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [t, setT] = useState(emptyT);
  const [saOpen, setSaOpen] = useState(false);
  const [sa, setSa] = useState({ name: "", email: "", password: "" });
  const [adOpen, setAdOpen] = useState(false);
  const [adTenant, setAdTenant] = useState(null);
  const [adUsers, setAdUsers] = useState([]);
  const [adForm, setAdForm] = useState({ name: "", email: "", password: "", role: "admin", status: "active" });
  const [editingUid, setEditingUid] = useState(null);

  const load = () => {
    api.get("/saas/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/saas/superadmins").then((r) => setAdmins(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setT(emptyT); setOpen(true); };
  const openEdit = (x) => { setEditing(x); setT({ name: x.name, plan: x.plan || "Starter", subdomain: x.slug || "", city: x.city || "", owner_email: "", owner_password: "" }); setOpen(true); };

  const saveTenant = async () => {
    if (!t.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      if (editing) { await api.patch(`/saas/tenants/${editing.id}`, t); toast.success("Empresa actualizada"); }
      else { await api.post("/saas/tenants", t); toast.success("Empresa creada"); }
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar"); }
  };

  const toggleStatus = async (x) => {
    try {
      await api.patch(`/saas/tenants/${x.id}`, { name: x.name, plan: x.plan, status: x.status === "active" ? "suspended" : "active" });
      toast.success(x.status === "active" ? "Empresa suspendida" : "Empresa activada");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const createSa = async () => {
    if (!sa.name.trim() || !sa.email.trim()) { toast.error("Nombre y correo requeridos"); return; }
    if (sa.password.length < 6) { toast.error("Contraseña mínima de 6 caracteres"); return; }
    try {
      await api.post("/saas/superadmins", sa);
      toast.success("Súper administrador creado");
      setSaOpen(false); setSa({ name: "", email: "", password: "" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo crear"); }
  };

  const openAdmins = async (x) => {
    setAdTenant(x); setEditingUid(null); setAdForm({ name: "", email: "", password: "", role: "admin", status: "active" }); setAdOpen(true);
    try { const r = await api.get(`/saas/tenants/${x.id}/users`); setAdUsers(r.data); } catch { setAdUsers([]); }
  };
  const refreshAdmins = async () => { const r = await api.get(`/saas/tenants/${adTenant.id}/users`); setAdUsers(r.data); };
  const editAdmin = (u) => { setEditingUid(u.user_id); setAdForm({ name: u.name, email: u.email, password: "", role: u.role === "tenant_owner" ? "tenant_owner" : "admin", status: u.status || "active" }); };
  const saveAdmin = async () => {
    if (!adForm.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      if (editingUid) {
        await api.patch(`/saas/tenants/${adTenant.id}/users/${editingUid}`, adForm);
        toast.success("Administrador actualizado");
      } else {
        if (!adForm.email.trim()) { toast.error("Correo requerido"); return; }
        if (adForm.password.length < 6) { toast.error("Contraseña mínima de 6 caracteres"); return; }
        await api.post(`/saas/tenants/${adTenant.id}/users`, adForm);
        toast.success("Administrador creado");
      }
      await refreshAdmins();
      setEditingUid(null);
      setAdForm({ name: "", email: "", password: "", role: "admin", status: "active" });
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar"); }
  };
  const toggleAdmin = async (u) => {
    try {
      await api.patch(`/saas/tenants/${adTenant.id}/users/${u.user_id}`, { name: u.name, role: u.role, status: u.status === "active" ? "suspended" : "active" });
      toast.success(u.status === "active" ? "Administrador suspendido" : "Administrador activado");
      await refreshAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFB]">
      <header className="border-b border-slate-200 bg-[#0A0A0B]">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3"><Logo dark /><span className="hidden rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-white md:inline-flex">Administración de la plataforma</span></div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Button asChild variant="ghost" size="sm" className="text-slate-300 hover:text-white"><Link to="/admin"><ArrowLeft className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Panel de administración</span></Link></Button>
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white" onClick={() => { logout(); nav("/"); }} data-testid="saas-logout"><LogOut className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Cerrar sesión</span></Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div><h1 className="text-2xl font-bold tracking-tight text-slate-900">Panel general</h1><p className="text-sm text-slate-500">Administra todas las empresas de la plataforma</p></div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50" data-testid="cleanup-btn"
              onClick={async () => {
                if (!window.confirm("Se eliminarán todas las empresas excepto Círculo Dorado y todos los usuarios excepto fernando.mendez@informexti.com, sisteinformaticos@gmail.com e info@informexti.com. ¿Continuar?")) return;
                try { const r = await api.post("/saas/cleanup"); toast.success(`Limpieza realizada: ${r.data.tenants} empresas y ${r.data.users} usuarios eliminados`); load(); }
                catch (e) { toast.error(e.response?.data?.detail || "No se pudo limpiar"); }
              }}>Limpiar datos de prueba</Button>
            <Button onClick={openNew} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="new-tenant-btn"><Plus className="mr-1.5 h-4 w-4" />Crear empresa</Button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Empresas activas" value={`${d.active}/${d.tenants}`} icon={Building2} />
          <StatCard label="MRR" value={money(d.mrr)} icon={DollarSign} tone="success" />
          <StatCard label="ARPA" value={money(d.arpa)} icon={TrendingUp} />
          <StatCard label="Almacenamiento" value={`${d.storage_gb} GB`} icon={HardDrive} />
        </div>

        <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-4"><h3 className="font-semibold text-slate-900">Organizaciones</h3></div>
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
              <tr><th className="px-5 py-3">Organización</th><th className="px-5 py-3">Plan</th><th className="px-5 py-3">Dominio</th><th className="px-5 py-3">Estado</th><th className="px-5 py-3 text-right">Acciones</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {d.items.map((x) => (
                <tr key={x.id} data-testid={`tenant-row-${x.id}`}>
                  <td className="px-5 py-4"><div className="font-semibold text-slate-900">{x.name}</div><div className="text-xs text-slate-400">{x.city || "—"}</div></td>
                  <td className="px-5 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${PLAN_CLS[x.plan] || PLAN_CLS.Starter}`}>{x.plan}</span></td>
                  <td className="px-5 py-4 font-mono text-xs text-slate-500">{x.domain || x.subdomain || "—"}</td>
                  <td className="px-5 py-4">{x.status === "active" ? <StatusBadge label="Activo" cls="bg-emerald-50 text-emerald-700" /> : <StatusBadge label="Suspendido" cls="bg-red-50 text-red-700" />}</td>
                  <td className="px-5 py-4"><div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => openAdmins(x)} data-testid={`admins-tenant-${x.id}`}>Admins</Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(x)} data-testid={`edit-tenant-${x.id}`}>Editar</Button>
                    <Button size="sm" variant="outline" onClick={() => toggleStatus(x)} data-testid={`toggle-tenant-${x.id}`}>{x.status === "active" ? "Suspender" : "Activar"}</Button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="superadmins-section">
          <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4"><Crown className="h-4 w-4 text-violet-600" /><h3 className="font-semibold text-slate-900">Súper administradores</h3><Button size="sm" variant="outline" className="ml-auto" onClick={() => setSaOpen(true)} data-testid="new-superadmin-btn"><ShieldPlus className="mr-1.5 h-3.5 w-3.5" />Nuevo súper admin</Button></div>
          <table className="w-full min-w-[520px] text-sm">
            <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400"><tr><th className="px-5 py-3">Nombre</th><th className="px-5 py-3">Correo</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {admins.map((a) => (<tr key={a.user_id} data-testid={`sa-row-${a.email}`}><td className="px-5 py-3 font-semibold text-slate-900">{a.name}</td><td className="px-5 py-3 text-slate-600">{a.email}</td></tr>))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="tenant-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar empresa" : "Crear empresa"}</DialogTitle><DialogDescription>Configura la empresa y, opcionalmente, su propietario inicial.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} className="mt-1.5" data-testid="nt-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Plan</Label><Select value={t.plan} onValueChange={(v) => setT({ ...t, plan: v })}><SelectTrigger className="mt-1.5" data-testid="nt-plan"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Starter">Starter</SelectItem><SelectItem value="Pro">Pro</SelectItem><SelectItem value="Enterprise">Enterprise</SelectItem></SelectContent></Select></div>
              <div><Label>Ciudad</Label><Input value={t.city} onChange={(e) => setT({ ...t, city: e.target.value })} className="mt-1.5" /></div>
            </div>
            {!editing && <div><Label>Subdominio</Label><div className="mt-1.5 flex items-center gap-2"><Input value={t.subdomain} onChange={(e) => setT({ ...t, subdomain: e.target.value })} placeholder="miorg" data-testid="nt-subdomain" /><span className="text-sm text-slate-400">.atlas.app</span></div></div>}
            {!editing && <>
              <div><Label>Correo del propietario (opcional)</Label><Input type="email" value={t.owner_email} onChange={(e) => setT({ ...t, owner_email: e.target.value })} className="mt-1.5" data-testid="nt-owner-email" /></div>
              <div><Label>Contraseña del propietario (opcional)</Label><Input type="password" value={t.owner_password} onChange={(e) => setT({ ...t, owner_password: e.target.value })} className="mt-1.5" placeholder="Mín. 6 caracteres" /></div>
            </>}
          </div>
          <div className="mt-4 flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={saveTenant} className="bg-brand hover:bg-brand-hover" data-testid="nt-create">{editing ? "Guardar" : "Crear y activar"}</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={adOpen} onOpenChange={setAdOpen}>
        <DialogContent data-testid="tenant-admins-dialog">
          <DialogHeader><DialogTitle>Administradores de {adTenant?.name}</DialogTitle><DialogDescription>Crea y administra los usuarios administradores/propietarios de esta empresa.</DialogDescription></DialogHeader>
          <div className="max-h-40 space-y-1.5 overflow-y-auto">
            {adUsers.length === 0 ? <p className="text-sm text-slate-400">Aún no hay administradores en esta empresa.</p> :
              adUsers.map((u) => (
                <div key={u.user_id} data-testid={`tadmin-${u.email}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <div className="min-w-0"><div className="truncate font-medium text-slate-900">{u.name} {u.status === "suspended" && <span className="ml-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600">Suspendido</span>}</div><div className="truncate text-xs text-slate-500">{u.email} · {u.role === "tenant_owner" ? "Propietario" : "Administrador"}</div></div>
                  <div className="flex shrink-0 gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => editAdmin(u)} data-testid={`edit-tadmin-${u.email}`}>Editar</Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => toggleAdmin(u)} data-testid={`toggle-tadmin-${u.email}`}>{u.status === "active" ? "Suspender" : "Activar"}</Button>
                  </div>
                </div>))}
          </div>
          <div className="mt-2 space-y-3 border-t border-slate-100 pt-4">
            <p className="text-xs font-semibold text-slate-500">{editingUid ? "Editar administrador" : "Nuevo administrador"}</p>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Nombre" value={adForm.name} onChange={(e) => setAdForm({ ...adForm, name: e.target.value })} data-testid="tadmin-name" />
              <Input type="email" placeholder="Correo" value={adForm.email} disabled={!!editingUid} onChange={(e) => setAdForm({ ...adForm, email: e.target.value })} data-testid="tadmin-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input type="password" placeholder={editingUid ? "Nueva contraseña (opcional)" : "Contraseña (mín. 6)"} value={adForm.password} onChange={(e) => setAdForm({ ...adForm, password: e.target.value })} data-testid="tadmin-password" />
              <Select value={adForm.role} onValueChange={(v) => setAdForm({ ...adForm, role: v })}><SelectTrigger data-testid="tadmin-role"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="admin">Administrador</SelectItem><SelectItem value="tenant_owner">Propietario</SelectItem></SelectContent></Select>
            </div>
            <div className="flex justify-end gap-2">
              {editingUid && <Button variant="outline" onClick={() => { setEditingUid(null); setAdForm({ name: "", email: "", password: "", role: "admin", status: "active" }); }} data-testid="tadmin-cancel">Cancelar</Button>}
              <Button onClick={saveAdmin} className="bg-brand hover:bg-brand-hover" data-testid="tadmin-create">{editingUid ? "Guardar cambios" : "Crear administrador"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={saOpen} onOpenChange={setSaOpen}>
        <DialogContent data-testid="superadmin-dialog">
          <DialogHeader><DialogTitle>Nuevo súper administrador</DialogTitle><DialogDescription>Tendrá acceso total a la plataforma.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input value={sa.name} onChange={(e) => setSa({ ...sa, name: e.target.value })} className="mt-1.5" data-testid="sa-name" /></div>
            <div><Label>Correo</Label><Input type="email" value={sa.email} onChange={(e) => setSa({ ...sa, email: e.target.value })} className="mt-1.5" data-testid="sa-email" /></div>
            <div><Label>Contraseña</Label><Input type="password" value={sa.password} onChange={(e) => setSa({ ...sa, password: e.target.value })} className="mt-1.5" placeholder="Mín. 6 caracteres" data-testid="sa-password" /></div>
          </div>
          <div className="mt-4 flex justify-end gap-2"><Button variant="outline" onClick={() => setSaOpen(false)}>Cancelar</Button><Button onClick={createSa} className="bg-brand hover:bg-brand-hover" data-testid="sa-create">Crear</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
